import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import '@fontsource/rubik';
import { updateState } from '../../../redux/commonSlice';
import usePharoahVisibility from '../../dashboard/hooks/usePharoahVisibility';
import HideOnPharoahConfirmationPopup from '../../dashboard/components/HideOnPharoahConfirmationPopup';

/**
 * Hide / show a project on the dashboard, for the project detail screen. Admin only —
 * every endpoint behind it rejects other roles.
 *
 * Props:
 *   project    — needs id, name, hidden_on_pharoah, status_type_order
 *   onChanged  — called with the new hidden value after a successful toggle
 *   showHelperText — render the "Dashboard only" line under the control (default true)
 */
const HideOnPharoahControl = ({ project, onChanged, showHelperText = true }) => {
  const dispatch = useDispatch();
  const commonReducer = useSelector((state) => state.root.commonReducer);
  const { toggleVisibility, isToggling } = usePharoahVisibility();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAdmin = commonReducer?.role_name === 'Admin';
  const isHidden = !!project?.hidden_on_pharoah;

  if (!isAdmin || !project?.id) return null;

  const confirm = () => {
    const nextHidden = !isHidden;
    toggleVisibility(
      { eventId: project.id, hidden: nextHidden, statusTypeOrder: project?.status_type_order },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          dispatch(
            updateState({
              pharoah_visibility_undo: {
                event_id: project.id,
                name: project?.name,
                hidden: nextHidden,
                status_type_order: project?.status_type_order,
                at: Date.now(),
              },
            })
          );
          onChanged && onChanged(nextHidden);
        },
      }
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Button
        variant="text"
        color="secondary"
        data-testid="detail-hide-pharoah-button"
        onClick={() => setConfirmOpen(true)}
        disabled={isToggling === project.id}
        startIcon={
          isHidden ? (
            <VisibilityOutlinedIcon sx={{ fontSize: '13px !important' }} />
          ) : (
            <VisibilityOffOutlinedIcon sx={{ fontSize: '13px !important' }} />
          )
        }
        sx={{
          fontSize: { xs: '8px', sm: '9px', md: '10px', lg: '11px', xl: '16px' },
          fontWeight: 600,
          height: { xs: '18px', sm: '20px', md: '22px', lg: '24px', xl: '32px' },
          fontFamily: 'Rubik',
          textTransform: 'none',
          letterSpacing: '0.65px',
          whiteSpace: 'nowrap',
        }}
      >
        {isHidden ? 'Show on dashboard' : 'Hide from dashboard'}
      </Button>

      {showHelperText && (
        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: { xs: '8px', md: '9px', lg: '10px', xl: '13px' },
            color: '#8A909C',
            maxWidth: { xs: '160px', md: '220px', xl: '320px' },
            lineHeight: 1.3,
          }}
        >
          Dashboard only — the client still sees this project in QueenBee.
        </Typography>
      )}

      {confirmOpen && (
        <HideOnPharoahConfirmationPopup
          open={confirmOpen}
          projectName={project?.name}
          hidden={!isHidden}
          isSubmitting={isToggling === project.id}
          onConfirm={confirm}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </Box>
  );
};

export default HideOnPharoahControl;
